import React, { useState, useRef, type ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, ChevronRight } from 'lucide-react';
import Dialog from '../../shared/custom-ui/dialog/dialog';
import { V2Button } from '../../shared/custom-ui/v2-components';
import { useV2Theme } from '../../providers/theme/ThemeProvider';
import { ReactComponent as LightThemeLogo } from '../../../../ui/src/assets/svgs/symbian-logo.svg';
import SymbianSmallLogo from '../../../../ui/src/assets/svgs/Symbian-logo-small.png';

const NavLinks = NavLink as any;
const Icons = { LogOut } as Record<string, any>;

/* ─── Types ───────────────────────────────────────────────────── */
export interface SidebarSubItem {
    key: string;
    label: string;
    path: string;
    icon?: React.ElementType;
    accent?: string;
    children?: SidebarSubItem[];
}

export interface SidebarNavItem {
    key: string;
    label: string;
    icon: React.ElementType;
    path: string;
    children?: SidebarSubItem[];
}

export interface SidebarProps {
    /** Navigation items */
    items: SidebarNavItem[];
    /** Whether sidebar is collapsed (mini rail) */
    collapsed: boolean;
    /** Callback to set collapsed state */
    setCollapsed?: (v: boolean) => void;
    /** Navigate handler — if omitted, uses NavLink internally */
    onNavigate?: (path: string) => void;
    /** Sign-out handler */
    onLogout?: () => void;
    /** Footer content (custom — replaces default logout) */
    footer?: ReactNode;
    /** QuickUpload component (show when files are dragged) */
    quickUploadComponent?: ReactNode;
    /** Whether a file drag is in progress */
    isDraggingOver?: boolean;
    /** Custom className */
    className?: string;
    /** Header height so logo section matches */
    headerHeight?: number;
    /** While true (role not yet resolved from /me), show skeleton instead of nav items */
    roleLoading?: boolean;
}

export const RAIL_W = 216;
export const MINI_W = 64;

const SubItem: React.FC<{ item: SidebarSubItem; onNavigate?: (p: string) => void }> = ({ item, onNavigate }) => {
    const { pathname } = useLocation();
    const { isDark } = useV2Theme();
    const normalizePath = (p: string) => (p || '/').replace(/\/+$/, '') || '/';
    const normalItem = normalizePath(item.path ?? '');
    const normalCurrent = normalizePath(pathname);
    const active = normalItem === '/'
        ? normalCurrent === '/'
        : normalCurrent === normalItem || normalCurrent.startsWith(normalItem + '/');
    const SubIcon = item.icon;

    const hasChildren = Boolean(item.children?.length);
    const isGroupActive = hasChildren
        ? item.children!.some((c) => {
            const nc = normalizePath(c.path ?? '');
            return nc === '/' ? normalCurrent === '/' : normalCurrent === nc || normalCurrent.startsWith(nc + '/');
        })
        : active;
    const [open, setOpen] = useState(isGroupActive);

    const row = (
        <div
            className="flex items-center gap-2.5 cursor-pointer text-[14px] tracking-[-0.005em] rounded-[var(--v2-radius-md)] py-[7px] pr-[10px] pl-[8px] transition-all duration-[var(--v2-duration-fast)]"
            style={{
                borderLeft: (hasChildren ? isGroupActive : active) ? '2.5px solid var(--v2-primary-500)' : '2.5px solid transparent',
                background: (hasChildren ? isGroupActive : active) ? (isDark ? 'rgba(4,94,184,0.26)' : 'rgba(13,71,161,0.08)') : 'transparent',
                color: (hasChildren ? isGroupActive : active) ? (isDark ? 'var(--v2-text-heading)' : 'var(--v2-primary-600)') : 'var(--v2-text-secondary)',
                fontWeight: (hasChildren ? isGroupActive : active) ? 650 : 450,
            }}
            onMouseEnter={(e) => {
                if (!(hasChildren ? isGroupActive : active)) {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                    el.style.color = 'var(--v2-text-primary)';
                    el.style.borderLeftColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
                }
            }}
            onMouseLeave={(e) => {
                if (!(hasChildren ? isGroupActive : active)) {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background = 'transparent';
                    el.style.color = 'var(--v2-text-secondary)';
                    el.style.borderLeftColor = 'transparent';
                }
            }}
        >
            {/* Icon */}
            <div
                className="flex w-[22px] h-[22px] items-center justify-center shrink-0 rounded-[var(--v2-radius-sm)] transition-[background] duration-[var(--v2-duration-fast)]"
                style={{
                    background: (hasChildren ? isGroupActive : active) ? (isDark ? 'rgba(4,94,184,0.22)' : 'rgba(13,71,161,0.10)') : 'transparent',
                }}
            >
                {SubIcon
                    ? <SubIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                    : <svg viewBox="0 0 6 6" className="w-[6px] h-[6px] shrink-0">
                        <circle cx="3" cy="3" r={(hasChildren ? isGroupActive : active) ? 3 : 2}
                            fill={(hasChildren ? isGroupActive : active) ? 'var(--v2-primary-500)' : (isDark ? 'rgba(255,255,255,0.30)' : 'rgba(13,71,161,0.30)')} />
                    </svg>
                }
            </div>
            <span className="flex-1 truncate leading-none">{item.label}</span>
            {hasChildren && (
                <ChevronRight
                    className="shrink-0 opacity-50 transition-transform duration-[var(--v2-duration-fast)]"
                    style={{ width: 12, height: 12, transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
                />
            )}
        </div>
    );

    if (!hasChildren) {
        return (
            <NavLinks
                to={item.path}
                onClick={(e: React.MouseEvent) => {
                    if (onNavigate) { e.preventDefault(); onNavigate(item.path); }
                }}
                className="block no-underline mb-[3px]"
                style={{ textDecoration: 'none' }}
            >
                {row}
            </NavLinks>
        );
    }

    return (
        <div className="mb-[3px]">
            <button
                type="button"
                onClick={() => setOpen((p) => !p)}
                aria-expanded={open}
                className="block w-full border-0 bg-transparent p-0 text-left cursor-pointer"
            >
                {row}
            </button>
            <div
                className="overflow-hidden"
                style={{
                    maxHeight: open ? 400 : 0,
                    opacity: open ? 1 : 0,
                    transition: `max-height 0.24s var(--v2-ease-calm), opacity 0.18s var(--v2-ease-calm)`,
                }}
            >
                <div className="pl-[14px] pt-[3px]">
                    {item.children!.map((child) => (
                        <SubItem key={child.key} item={child} onNavigate={onNavigate} />
                    ))}
                </div>
            </div>
        </div>
    );
};

const FlyoutRow: React.FC<{
    item: SidebarSubItem;
    pathname: string;
    isDark: boolean;
    onSelect: () => void;
}> = ({ item, pathname, isDark, onSelect }) => {
    const normalizePath = (p: string) => (p || '/').replace(/\/+$/, '') || '/';
    const normalItem = normalizePath(item.path ?? '');
    const normalCurrent = normalizePath(pathname);
    const isActive = normalItem === '/'
        ? normalCurrent === '/'
        : normalCurrent === normalItem || normalCurrent.startsWith(normalItem + '/');
    const ItemIcon = item.icon;

    return (
        <div
            onClick={onSelect}
            className="flex items-center gap-[9px] px-[10px] py-[8px] rounded-[var(--v2-radius-md)] cursor-pointer text-[13.5px] select-none transition-[background,color] duration-[120ms]"
            style={{
                fontWeight: isActive ? 650 : 450,
                color: isActive ? (isDark ? 'var(--v2-text-heading)' : 'var(--v2-primary-600)') : 'var(--v2-text-secondary)',
                background: isActive ? (isDark ? 'rgba(4,94,184,0.22)' : 'rgba(13,71,161,0.08)') : 'transparent',
            }}
            onMouseEnter={(e) => {
                if (!isActive) {
                    (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--v2-text-primary)';
                }
            }}
            onMouseLeave={(e) => {
                if (!isActive) {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = 'var(--v2-text-secondary)';
                }
            }}
        >
            {/* Icon */}
            <div
                className="w-[22px] h-[22px] shrink-0 flex items-center justify-center rounded-[var(--v2-radius-sm)]"
                style={{
                    background: isActive ? (isDark ? 'rgba(4,94,184,0.22)' : 'rgba(13,71,161,0.10)') : 'transparent',
                }}
            >
                {ItemIcon
                    ? <ItemIcon style={{ width: 13, height: 13 }} />
                    : <svg viewBox="0 0 6 6" className="w-[6px] h-[6px]">
                        <circle cx="3" cy="3" r={isActive ? 3 : 2}
                            fill={isActive ? 'var(--v2-primary-500)' : (isDark ? 'rgba(255,255,255,0.30)' : 'rgba(13,71,161,0.30)')} />
                    </svg>
                }
            </div>
            <span className="flex-1 truncate">{item.label}</span>
            {isActive && (
                <div className="w-[5px] h-[5px] rounded-full bg-[var(--v2-primary-500)] shrink-0" />
            )}
        </div>
    );
};

const GroupItem: React.FC<{
    item: SidebarNavItem;
    collapsed: boolean;
    onNavigate?: (p: string) => void;
}> = ({ item, collapsed, onNavigate }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isDark } = useV2Theme();
    const normalizePath = (p: string) => (p || '/').replace(/\/+$/, '') || '/';
    const GroupIcon = item.icon;

    const isGroupActive = item.children
        ? item.children.some((c) => normalizePath(pathname).startsWith(normalizePath(c.path)))
        : normalizePath(pathname) === normalizePath(item.path) || normalizePath(pathname).startsWith(normalizePath(item.path) + '/');

    const [open, setOpen] = useState(isGroupActive);
    const [flyoutOpen, setFlyoutOpen] = useState(false);
    const [flyoutTop, setFlyoutTop] = useState(0);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const flyoutRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Close flyout on outside click
    React.useEffect(() => {
        if (!flyoutOpen) return;
        const handler = (e: MouseEvent) => {
            if (
                flyoutRef.current && !flyoutRef.current.contains(e.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(e.target as Node)
            ) {
                setFlyoutOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [flyoutOpen]);

    const handleCollapsedClick = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFlyoutTop(rect.top);
        }
        setFlyoutOpen(prev => !prev);
    };

    const toggleOpen = () => setOpen((p) => !p);

    return (
        <li className="relative mb-[2px] group">
            {/* Group trigger row */}
            <button
                ref={buttonRef}
                type="button"
                onClick={collapsed ? handleCollapsedClick : toggleOpen}
                title={item.label}
                aria-expanded={collapsed ? flyoutOpen : open}
                className="flex w-full items-center border-0 shadow-none cursor-pointer text-left text-[15.5px] tracking-[-0.01em] rounded-[var(--v2-radius-md)]"
                style={{
                    gap: collapsed ? 0 : 11,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    padding: collapsed ? '10px 0' : '10px 11px',
                    borderLeft: (isGroupActive && !collapsed) ? '3px solid var(--v2-primary-500)' : '3px solid transparent',
                    fontWeight: isGroupActive ? 700 : 500,
                    color: isGroupActive ? (isDark ? 'var(--v2-text-heading)' : 'var(--v2-primary-600)') : 'var(--v2-text-secondary)',
                    background: (isGroupActive || (collapsed && flyoutOpen)) ? (isDark ? 'rgba(4,94,184,0.30)' : 'rgba(13,71,161,0.10)') : 'transparent',
                    transition: `background var(--v2-duration-fast) var(--v2-ease-calm), color var(--v2-duration-fast) var(--v2-ease-calm)`,
                }}
                onMouseEnter={(e) => {
                    if (!isGroupActive && !(collapsed && flyoutOpen)) {
                        (e.currentTarget as HTMLElement).style.background = 'var(--v2-sidebar-hover)';
                        (e.currentTarget as HTMLElement).style.color = 'var(--v2-text-primary)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isGroupActive && !(collapsed && flyoutOpen)) {
                        (e.currentTarget as HTMLElement).style.background = 'transparent';
                        (e.currentTarget as HTMLElement).style.color = 'var(--v2-text-secondary)';
                    }
                }}
            >
                {/* Icon */}
                <div className="flex h-8 w-8 items-center justify-center shrink-0 rounded-[var(--v2-radius-sm)] bg-transparent transition-all duration-[var(--v2-duration-fast)]">
                    <GroupIcon style={{ height: 19, width: 19, flexShrink: 0 }} />
                </div>

                {!collapsed && (
                    <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.children && (
                            <ChevronRight
                                className="shrink-0 opacity-50 transition-transform duration-[var(--v2-duration-fast)]"
                                style={{
                                    width: 14, height: 14,
                                    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            />
                        )}
                    </>
                )}

                {/* Tooltip (collapsed, flyout closed) */}
                {collapsed && !flyoutOpen && (
                    <span
                        className="opacity-0 group-hover:opacity-100 absolute whitespace-nowrap rounded-[var(--v2-radius-md)] text-[14px] font-medium border border-[var(--v2-border-glass)] px-[16px] py-[9px] bg-[var(--v2-glass-dropdown)] text-[var(--v2-text-primary)] z-50 transition-opacity duration-[var(--v2-duration-fast)] pointer-events-none"
                        style={{
                            left: '100%', marginLeft: 12,
                            boxShadow: 'var(--v2-shadow-card)',
                            backdropFilter: 'blur(16px)',
                        }}
                    >
                        {item.label}
                    </span>
                )}
            </button>

            {/* ── Collapsed flyout submenu (portal — escapes backdrop-filter stacking context) ── */}
            {collapsed && flyoutOpen && item.children && ReactDOM.createPortal(
                <div
                    ref={flyoutRef}
                    className="fixed min-w-[220px] rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass)] bg-[var(--v2-glass-dropdown)] p-[6px]"
                    style={{
                        left: 76,
                        top: flyoutTop,
                        zIndex: 9999,
                        backdropFilter: 'blur(20px) saturate(1.2)',
                        WebkitBackdropFilter: 'blur(20px) saturate(1.2)',
                        boxShadow: 'var(--v2-shadow-card)',
                        animation: 'v2FlyoutIn 0.14s var(--v2-ease-calm) both',
                    }}
                >
                    {/* Section header */}
                    <div className="px-[10px] pt-[6px] pb-[8px] text-[11px] font-bold tracking-[0.06em] uppercase text-[var(--v2-text-muted)] border-b border-[var(--v2-border-subtle)] mb-[4px]">
                        {item.label}
                    </div>

                    {item.children.flatMap((child) => {
                        if (child.children?.length) {
                            return [
                                <div key={`${child.key}-label`} className="px-[10px] pt-[8px] pb-[3px] text-[10px] font-bold tracking-[0.06em] uppercase text-[var(--v2-text-muted)]">
                                    {child.label}
                                </div>,
                                ...child.children.map((grandchild) => (
                                    <FlyoutRow key={grandchild.key} item={grandchild} pathname={pathname} isDark={isDark}
                                        onSelect={() => { setFlyoutOpen(false); if (onNavigate) onNavigate(grandchild.path); else navigate(grandchild.path); }} />
                                )),
                            ];
                        }
                        return [
                            <FlyoutRow key={child.key} item={child} pathname={pathname} isDark={isDark}
                                onSelect={() => { setFlyoutOpen(false); if (onNavigate) onNavigate(child.path); else navigate(child.path); }} />,
                        ];
                    })}
                </div>,
                document.body
            )}

            {/* Accordion children (expanded mode only) */}
            {!collapsed && item.children && (
                <div
                    ref={listRef}
                    className="overflow-hidden"
                    style={{
                        maxHeight: open ? 500 : 0,
                        opacity: open ? 1 : 0,
                        transition: `max-height 0.28s var(--v2-ease-calm), opacity 0.2s var(--v2-ease-calm)`,
                    }}
                >
                    <div className="pl-[18px] pt-[6px] pb-[6px]">
                        {item.children.map((child) => (
                            <SubItem key={child.key} item={child} onNavigate={onNavigate} />
                        ))}
                    </div>
                </div>
            )}
        </li>
    );
};

const Sidebar: React.FC<SidebarProps> = ({
    items,
    collapsed,
    onNavigate,
    onLogout,
    footer,
    quickUploadComponent,
    isDraggingOver = false,
    className = '',
    headerHeight = 72,
    roleLoading = false,
}) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isDark } = useV2Theme();
    const [confirmLogout, setConfirmLogout] = useState(false);
    const logoutBtnRef = useRef<HTMLButtonElement | null>(null);

    const normalizePath = (p: string) => (p || '/').replace(/\/+$/, '') || '/';

    const isActive = (path: string) => {
        const cur = normalizePath(pathname);
        const base = normalizePath(path);
        if (base === '/') return cur === '/';
        return cur === base || cur.startsWith(base + '/');
    };

    return (
        <nav
            className={`relative h-full flex flex-col overflow-hidden overflow-x-hidden
             bg-[var(--v2-glass-sidebar)] border-r border-[var(--v2-border-card)]
              ${className}`.trim()}
            style={{
                backdropFilter: 'blur(20px) saturate(1.15)',
                WebkitBackdropFilter: 'blur(20px) saturate(1.15)',
            }}
            role="navigation"
            aria-label="Primary navigation"
        >
            {/* ── Logo Header ─────────────────────────────────── */}
            <div
                className="flex items-center shrink-0 overflow-hidden cursor-pointer bg-[var(--v2-bg-card)]"
                style={{
                    height: headerHeight,
                    minHeight: headerHeight,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    padding: collapsed ? '0' : '0 20px',
                    borderBottom: '1.5px solid var(--v2-border-card)',
                }}
                onClick={() => navigate('/')}
                title="Go to Dashboard"
            >
                {collapsed ? (
                    <img
                        src={SymbianSmallLogo}
                        alt="Symbian Health"
                        className="h-[52px] w-[52px] object-contain shrink-0 block border-0 outline-none shadow-none bg-transparent"
                        style={{ filter: isDark ? 'brightness(0) invert(1)' : 'none' }}
                    />
                ) : (
                    <LightThemeLogo
                        className="max-w-[160px]"
                        style={{ height: 38, width: 'auto', filter: isDark ? 'brightness(0) invert(1)' : 'none' }}
                        aria-label="Symbian Health"
                        preserveAspectRatio="xMidYMid meet"
                    />
                )}
            </div>


            {!isDraggingOver ? (
                <>
                    {/* ── Navigation items ──────────────────────── */}
                    <ul
                        className="flex-1 overflow-y-auto overflow-x-hidden v2-scrollbar list-none m-0"
                        style={{
                            padding: collapsed ? `var(--v2-space-4) var(--v2-space-2)` : `var(--v2-space-4) var(--v2-space-3)`,
                        }}
                    >
                        {roleLoading ? (
                            [80, 60, 70, 90, 55, 65].map((w, i) => (
                                <li
                                    key={i}
                                    className="mb-[6px] flex items-center gap-3 rounded-[var(--v2-radius-md)] px-3 py-[9px]"
                                    style={{ background: 'var(--v2-bg-surface)' }}
                                >
                                    {/* Icon placeholder */}
                                    <div
                                        className="relative overflow-hidden w-[22px] h-[22px] rounded-[var(--v2-radius-sm)] flex-shrink-0"
                                        style={{ background: 'var(--v2-skeleton-base)' }}
                                    >
                                        <div
                                            className="absolute inset-0 -translate-x-full animate-shimmer"
                                            style={{
                                                backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)',
                                                animationDelay: `${i * 0.06}s`,
                                            }}
                                        />
                                    </div>
                                    {/* Label placeholder — hidden in mini-rail mode */}
                                    {!collapsed && (
                                        <div
                                            className="relative overflow-hidden h-[13px] rounded flex-1"
                                            style={{ maxWidth: `${w}%`, background: 'var(--v2-skeleton-base)' }}
                                        >
                                            <div
                                                className="absolute inset-0 -translate-x-full animate-shimmer"
                                                style={{
                                                    backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)',
                                                    animationDelay: `${i * 0.06 + 0.03}s`,
                                                }}
                                            />
                                        </div>
                                    )}
                                </li>
                            ))
                        ) : (
                            items.map((item) => {
                                if (item.children?.length) {
                                    return (
                                        <GroupItem key={item.key} item={item} collapsed={collapsed} onNavigate={onNavigate} />
                                    );
                                }

                                const ItemIcon = item.icon as any;
                                const active = isActive(item.path);

                                return (
                                    <li key={item.key} className="relative mb-[3px] group">
                                        <NavLinks
                                            to={item.path}
                                            end={item.path === '/'}
                                            title={item.label}
                                            aria-label={item.label}
                                            onClick={(e: React.MouseEvent) => {
                                                if (onNavigate) { e.preventDefault(); onNavigate(item.path); }
                                            }}
                                            className="flex items-center outline-none no-underline shadow-none rounded-[var(--v2-radius-md)] text-[15.5px] tracking-[-0.01em]"
                                            style={{
                                                gap: collapsed ? 0 : 11,
                                                justifyContent: collapsed ? 'center' : 'flex-start',
                                                padding: collapsed ? '10px 0' : '10px 11px',
                                                borderLeft: (active && !collapsed) ? '3px solid var(--v2-primary-500)' : '3px solid transparent',
                                                fontWeight: active ? 700 : 500,
                                                color: active ? (isDark ? 'var(--v2-text-heading)' : 'var(--v2-primary-600)') : 'var(--v2-text-secondary)',
                                                background: active ? (isDark ? 'rgba(4,94,184,0.30)' : 'rgba(13,71,161,0.10)') : 'transparent',
                                                transition: `all var(--v2-duration-fast) var(--v2-ease-calm)`,
                                            }}
                                            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                                                if (!active) {
                                                    e.currentTarget.style.background = isDark ? 'rgba(4,94,184,0.14)' : 'rgba(13,71,161,0.06)';
                                                    e.currentTarget.style.color = 'var(--v2-text-primary)';
                                                }
                                            }}
                                            onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                                                if (!active) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.color = 'var(--v2-text-secondary)';
                                                }
                                            }}
                                        >
                                            {/* Icon */}
                                            <div className="flex h-8 w-8 items-center justify-center shrink-0 rounded-[var(--v2-radius-sm)] bg-transparent transition-all duration-[var(--v2-duration-fast)]">
                                                <ItemIcon style={{ height: 19, width: 19, flexShrink: 0 }} />
                                            </div>

                                            {/* Label */}
                                            {!collapsed && <span className="flex-1 truncate">{item.label}</span>}

                                            {/* Tooltip (collapsed) */}
                                            {collapsed && (
                                                <span
                                                    className="opacity-0 group-hover:opacity-100 absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[var(--v2-radius-md)] text-[14px] font-medium border border-[var(--v2-border-glass)] px-[15px] py-[8px] bg-[var(--v2-glass-dropdown)] text-[var(--v2-text-primary)] z-[100] transition-opacity duration-[var(--v2-duration-fast)] pointer-events-none"
                                                    style={{
                                                        left: 'calc(100% + 14px)',
                                                        boxShadow: 'var(--v2-shadow-card)',
                                                        backdropFilter: 'blur(16px)',
                                                    }}
                                                >
                                                    {item.label}
                                                </span>
                                            )}
                                            {active && <span className="sr-only" aria-current="page">(current)</span>}
                                        </NavLinks>
                                    </li>
                                );
                            })
                        )}
                    </ul>

                    {/* ── Footer ──────────────────────────────────── */}
                    <div
                        className="border-t border-[var(--v2-border-subtle)]"
                        style={{
                            padding: collapsed ? 'var(--v2-space-4) var(--v2-space-2)' : 'var(--v2-space-4) var(--v2-space-3)',
                            paddingBottom: 'calc(var(--v2-space-4) + env(safe-area-inset-bottom, 0px))',
                        }}
                    >
                        {footer || (onLogout && (
                            <button
                                type="button"
                                ref={logoutBtnRef}
                                onClick={() => setConfirmLogout(true)}
                                aria-label="Open sign-out confirmation"
                                className="flex w-full items-center border-0 rounded-[var(--v2-radius-md)] text-[15.5px] font-medium tracking-[-0.01em] text-[var(--v2-danger-500)] bg-transparent cursor-pointer transition-all duration-[var(--v2-duration-fast)]"
                                style={{
                                    justifyContent: collapsed ? 'center' : 'flex-start',
                                    gap: collapsed ? 0 : 11,
                                    padding: collapsed ? '10px 0' : '10px 11px',
                                }}
                                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--v2-danger-50)'; }}
                                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                                <div className="flex w-8 h-8 items-center justify-center shrink-0">
                                    <Icons.LogOut style={{ height: 19, width: 19 }} />
                                </div>
                                {!collapsed && <span>Sign out</span>}
                            </button>
                        ))}
                    </div>
                </>
            ) : (
                <div className="flex flex-1 items-center justify-center p-6">
                    {quickUploadComponent || (
                        <div className="text-center text-[var(--v2-text-muted)] text-[13px]">Drop files here</div>
                    )}
                </div>
            )}

            {/* Logout confirmation */}
            <Dialog
                isOpen={confirmLogout}
                onClose={() => setConfirmLogout(false)}
                title="Sign out?"
                description="You're about to end your session. Any unsaved work may be lost."
                size="small"
                footer={
                    <div className="flex gap-3 justify-end">
                        <V2Button variant="ghost" onClick={() => setConfirmLogout(false)}>Cancel</V2Button>
                        <V2Button variant="danger" onClick={() => { setConfirmLogout(false); onLogout?.(); }}>Sign out</V2Button>
                    </div>
                }
            />
        </nav>
    );
};

export default Sidebar;
